<template>
  <div class="controls">
    <div class="control-panel">
      <h2>基本信息</h2>
      <div class="control-group">
        <!-- 真太阳时和四柱信息 -->
        <div class="info-row info-row--four-pillars">
          <span>四柱:  {{ formattedFateData.fourPillars || '待计算' }}</span>
        </div>
        <!-- 添加姓名和地址输入 -->
        <div class="control-row">
          <label>姓名:</label>
          <input type="text" v-model="requestData.name">
        </div>
        <div class="control-row">
          <label>地址:</label>
          <div class="address-field">
            <input type="text" v-model="requestData.address" class="address-input" style="direction: rtl;"/>
            <p class="mapButton" @click="openMapDialog">地图</p>
          </div>
          <!-- 地图模块-->
          <el-dialog :visible.sync="dialogVisible"
                     title="地图" @open="handLeDialogOpen"
                     fullscreen
                     custom-class="map-dialog-fullscreen"
                     :close-on-click-modal="false"
                     append-to-body
          >
            <div class="map-dialog-toolbar">
              <div class="map-dialog-fields">
                <!--详细地址-->
                <div class="map-dialog-field map-dialog-field--address">
                  <span>详细地址：</span>
                  <!-- 本框身兼两职：① 回显"详细地址"；② 地名检索输入框（2026-10-10 新增）。
                       输入关键字 → 高德联想下拉；回车或点「查询」→ 取首个匹配地点并定位。
                       在地图上点选坐标 → 逆地理编码结果会**直接覆盖本框**。 -->
                  <input id="latNum" type="text" class="map-dialog-input"
                         placeholder="输入地名/关键字查询，或点击地图选点"
                         autocomplete="off"
                         @keydown.enter.prevent="searchMapPlace('main')" />
                  <p class="mapButton mapButton--search" @click="searchMapPlace('main')">查询</p>
                </div>
                <!--纬度-->
                <div class="map-dialog-field">
                  <span>纬度：</span>
                  <input disabled id="lat" type="text" class="map-dialog-input map-dialog-input--coord"/>
                </div>
                <!--经度-->
                <div class="map-dialog-field">
                  <span>经度：</span>
                  <input disabled id="lng" type="text" class="map-dialog-input map-dialog-input--coord"/>
                </div>
              </div>
              <p class="mapButton" @click="closeMapDialog">确定</p>
              <!-- 全屏后右端的「关闭」：只关弹窗、不应用（不回写详细地址与经纬度），
                   口径与右上角 × 一致；「确定」仍是"应用并关闭" -->
              <p class="mapButton mapButton--close" @click="dismissMapDialog">关闭</p>
            </div>

            <div class="map-container-wrapper">
              <!--卫星地图开关：浮在地图内部左上角（高德示例的经典位置）-->
              <div class="input-card">
                <label>卫星地图：</label>
                <p class="btn" id="add-satellite-layer" style="margin-right:1rem;"
                        @click="showMapType(true)"
                >打开</p>
                <p class="btn" id="remove-satellite-layer"
                        @click="showMapType(false)"
                >关闭</p>
              </div>

              <!--地图：高度交给全屏弹窗的 flex 布局（见文件末尾非 scoped 样式）-->
              <div id="mapContainer" class="map-canvas"/>
            </div>
          </el-dialog>
        </div>
        <div class="control-row">
          <label>公历:</label>
          <input
            ref="date"
            type="text"
            v-model="requestData.date"
            inputmode="numeric"
            placeholder="YYYY.../MM/DD 或 -YYYY.../MM/DD(公元前)"
            @keydown="handleStrictMaskedKeydown($event, 'date')"
            @focus="handleStrictMaskedFocus($event, 'date')"
            @paste="handleStrictMaskedPaste($event, 'date')"
            @input="handleStrictMaskedInput('date')"
            @mouseup="handleStrictMaskedMouseUp($event, 'date')"
            @blur="handleStrictMaskedBlur('date')"
          >
        </div>
        <!-- 农历（万年历口径）：日月重合时刻落在北京时间 24:00 之前即算当天初一。
             本行在前端独立计算（utils/wannianliLunar.js）；用户可直接输入农历，
             由 handleLunarTextInput 换算成公历写回上方「公历」行（再用既有链路重新生成星盘）。
             换算用的是同一套 lunar-calendar（东八区/24:00 口径），与显示口径一致。 -->
        <div class="control-row">
          <label>农历:</label>
          <input
            type="text"
            v-model="requestData.lunarDate"
            placeholder="待计算，或输入如 丙午年,正月初一 / 2026-5-1（自动换算公历）"
            @input="handleLunarTextInput('basic')"
          >
        </div>
        <!-- 星雲鑑历：真太阳时（子时初）23:00 分界，由后端 /fateCalculate 计算回填。
             2026-10-18 起改为纯文本展示（不再是只读输入框）：它是计算结果的展示位，
             不该长得像可填表单，也不该被当成表单字段去编辑/复制。
             注意：这里绝不能挂 @input 做「农历→公历」换算，否则会改动星盘日期与计算。 -->
        <div class="control-row">
          <label class="label--xiangyunjianli">星雲鑑历:</label>
          <span
            class="control-row__text control-row__text--xyj"
            :class="{ 'control-row__text--empty': !requestData.lunarDateXYJ }"
          >{{ requestData.lunarDateXYJ || '待计算' }}</span>
        </div>
        <div class="control-row">
          <label>北京时间:</label>
          <input
            ref="time"
            type="text"
            v-model="requestData.time"
            inputmode="numeric"
            maxlength="8"
            placeholder="HH:MM:SS"
            @keydown="handleStrictMaskedKeydown($event, 'time')"
            @focus="handleStrictMaskedFocus($event, 'time')"
            @paste="handleStrictMaskedPaste($event, 'time')"
            @input="handleStrictMaskedInput('time')"
            @mouseup="handleStrictMaskedMouseUp($event, 'time')"
            @blur="handleStrictMaskedBlur('time')"
          >
        </div>
        <div class="info-row">
          <span>真太阳时:  {{ formattedFateData.trueSolarTime || '待计算' }}</span>
        </div>
        <div class="control-row">
          <label>时区:</label>
          <select v-model="requestData.timezone">
            <option v-for="tz in timezones" :key="tz.value" :value="tz.value">
              {{ tz.label }}
            </option>
          </select>
        </div>
        <!-- 修改经纬度输入部分 -->
        <div class="control-row">
          <label>经度:</label>
          <div class="coord-input">
            <input type="number" v-model.number="requestData.longitudeDeg" placeholder="度" min="0" max="180">
            <span>度</span>
            <input type="number" v-model.number="requestData.longitudeMin" placeholder="分" min="0" max="59">
            <span>分</span>
            <input type="number" v-model.number="requestData.longitudeSec" placeholder="秒" min="0" max="59">
            <span>秒</span>
          </div>
        </div>
        <div class="control-row">
          <label>纬度:</label>
          <div class="coord-input">
            <input type="number" v-model.number="requestData.latitudeDeg" placeholder="度" min="0" max="90">
            <span>度</span>
            <input type="number" v-model.number="requestData.latitudeMin" placeholder="分" min="0" max="59">
            <span>分</span>
            <input type="number" v-model.number="requestData.latitudeSec" placeholder="秒" min="0" max="59">
            <span>秒</span>
          </div>
        </div>
        <div class="control-row">
          <label>分宫制:</label>
          <select v-model="requestData.hsys">
            <option value="P">普拉西德制 (Placidus)</option>
            <option value="W">整宫制（古）</option>
            <option value="E">等宫制（英）</option>
            <option value="B">Alcabitius（赤黄）</option>
            <option value="R">Regiomontanus（卦）</option>
            <option value="O">Porphyry（高心）</option>
            <option value="K">Koch</option>

            <option value="C">坎帕纳斯制 (Campanus)</option>
            <option value="G">36高奎兰分区 (36 Gauquelin sectors)</option>
            <option value="H">地平/方位宫位制 (horizon / azimut)</option>
            <option value="M">莫里纳斯制 (Morinus)</option>
            <option value="T">波利奇/佩吉制 (topocentric)</option>
            <option value="U">克鲁辛斯基-皮萨-戈尔泽制 (Krusinski-Pisa-Goelzer)</option>
            <option value="V">等宫维赫洛制 (equal Vehlow)</option>
            <option value="X">轴旋转系统/子午线宫位制 (axial rotation system/ Meridian houses)</option>
            <option value="Y">APC宫位制 (APC houses)</option>
          </select>
        </div>
        <div class="control-row">
          <label>坐山:</label>
          <div class="coord-input">
            <input type="number" v-model.number="requestData.zuoshan" placeholder="度" step="0.1" min="0" max="360">
            <span>度</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 本命盘数据板块 -->
    <div class="control-panel natal-panel" v-if="['ecliptic', 'horizon_ecliptic'].includes(requestData.astrolabeType) && hasPermission('natal_chart')">
      <h2>
        本命盘信息
        <span class="collapse-btn" @click="toggleNatalPanel">
          {{ natalPanelCollapsed ? '▼ 展开' : '▲ 收起' }}
        </span>
      </h2>
      <div class="control-group" v-show="!natalPanelCollapsed">
        <!-- 四柱信息 -->
        <div class="info-row info-row--four-pillars">
          <span>四柱:  {{ formattedNatalFateData.fourPillars || '待计算' }}</span>
        </div>
        <!-- 姓名 -->
        <div class="control-row">
          <label>姓名:</label>
          <input type="text" v-model="requestData.natalName">
        </div>
        <div class="control-row">
          <label>地址:</label>
          <div class="address-field">
            <input type="text" v-model="requestData.natalAddress" class="address-input" style="direction: rtl;"/>
            <p class="mapButton" @click="openNatalMapDialog">地图</p>
          </div>
          <!-- 地图模块-->
          <el-dialog :visible.sync="natalDialogVisible"
                     title="地图" @open="handleNatalDialogOpen"
                     fullscreen
                     custom-class="map-dialog-fullscreen"
                     :close-on-click-modal="false"
                     append-to-body
          >
            <div class="map-dialog-toolbar">
              <div class="map-dialog-fields">
                <!--详细地址-->
                <div class="map-dialog-field map-dialog-field--address">
                  <span>详细地址：</span>
                  <!-- 同主盘地图：既是详细地址回显框，也是地名检索输入框（2026-10-10 新增） -->
                  <input id="natalLatNum" type="text" class="map-dialog-input"
                         placeholder="输入地名/关键字查询，或点击地图选点"
                         autocomplete="off"
                         @keydown.enter.prevent="searchMapPlace('natal')" />
                  <p class="mapButton mapButton--search" @click="searchMapPlace('natal')">查询</p>
                </div>
                <!--纬度-->
                <div class="map-dialog-field">
                  <span>纬度：</span>
                  <input disabled id="natalLat" type="text" class="map-dialog-input map-dialog-input--coord"/>
                </div>
                <!--经度-->
                <div class="map-dialog-field">
                  <span>经度：</span>
                  <input disabled id="natalLng" type="text" class="map-dialog-input map-dialog-input--coord"/>
                </div>
              </div>
              <p class="mapButton" @click="closeNatalMapDialog">确定</p>
              <!-- 同主盘：右端「关闭」只关弹窗、不应用 -->
              <p class="mapButton mapButton--close" @click="dismissNatalMapDialog">关闭</p>
            </div>

            <div class="map-container-wrapper">
              <!--卫星地图开关：浮在地图内部左上角（高德示例的经典位置）-->
              <div class="input-card">
                <label>卫星地图：</label>
                <p class="btn" id="add-natal-satellite-layer" style="margin-right:1rem;"
                        @click="showNatalMapType(true)"
                >打开</p>
                <p class="btn" id="remove-natal-satellite-layer"
                        @click="showNatalMapType(false)"
                >关闭</p>
              </div>

              <!--地图：高度交给全屏弹窗的 flex 布局（见文件末尾非 scoped 样式）-->
              <div id="natalMapContainer" class="map-canvas"/>
            </div>
          </el-dialog>
        </div>
        <div class="control-row">
          <label>公历:</label>
          <input
            ref="natalDate"
            type="text"
            v-model="requestData.natalDate"
            inputmode="numeric"
            placeholder="YYYY.../MM/DD 或 -YYYY.../MM/DD(公元前)"
            @keydown="handleStrictMaskedKeydown($event, 'natalDate')"
            @focus="handleStrictMaskedFocus($event, 'natalDate')"
            @paste="handleStrictMaskedPaste($event, 'natalDate')"
            @input="handleStrictMaskedInput('natalDate')"
            @mouseup="handleStrictMaskedMouseUp($event, 'natalDate')"
            @blur="handleStrictMaskedBlur('natalDate')"
          >
        </div>
        <!-- 本命盘：口径与主盘一致 —— 农历=万年历（可输入，自动换算本命盘公历），星雲鑑历=只读展示 -->
        <div class="control-row">
          <label>农历:</label>
          <input
            type="text"
            v-model="requestData.natalLunarDate"
            placeholder="待计算，或输入如 丙午年,正月初一 / 2026-5-1（自动换算公历）"
            @input="handleLunarTextInput('natal')"
          >
        </div>
        <!-- 本命盘星雲鑑历：与主盘同口径，纯文本展示（2026-10-18） -->
        <div class="control-row">
          <label class="label--xiangyunjianli">星雲鑑历:</label>
          <span
            class="control-row__text control-row__text--xyj"
            :class="{ 'control-row__text--empty': !requestData.natalLunarDateXYJ }"
          >{{ requestData.natalLunarDateXYJ || '待计算' }}</span>
        </div>
        <div class="control-row">
          <label>北京时间:</label>
          <input
            ref="natalTime"
            type="text"
            v-model="requestData.natalTime"
            inputmode="numeric"
            maxlength="8"
            placeholder="HH:MM:SS"
            @keydown="handleStrictMaskedKeydown($event, 'natalTime')"
            @focus="handleStrictMaskedFocus($event, 'natalTime')"
            @paste="handleStrictMaskedPaste($event, 'natalTime')"
            @input="handleStrictMaskedInput('natalTime')"
            @mouseup="handleStrictMaskedMouseUp($event, 'natalTime')"
            @blur="handleStrictMaskedBlur('natalTime')"
          >
        </div>
        <div class="info-row">
          <span>真太阳时:  {{ formattedNatalFateData.trueSolarTime || '待计算' }}</span>
        </div>
        <div class="control-row">
          <label>经度:</label>
          <div class="coord-input">
            <input type="number" v-model.number="requestData.natalLongitudeDeg" placeholder="度" min="0" max="180">
            <span>度</span>
            <input type="number" v-model.number="requestData.natalLongitudeMin" placeholder="分" min="0" max="59">
            <span>分</span>
            <input type="number" v-model.number="requestData.natalLongitudeSec" placeholder="秒" min="0" max="59">
            <span>秒</span>
          </div>
        </div>
        <div class="control-row">
          <label>纬度:</label>
          <div class="coord-input">
            <input type="number" v-model.number="requestData.natalLatitudeDeg" placeholder="度" min="0" max="90">
            <span>度</span>
            <input type="number" v-model.number="requestData.natalLatitudeMin" placeholder="分" min="0" max="59">
            <span>分</span>
            <input type="number" v-model.number="requestData.natalLatitudeSec" placeholder="秒" min="0" max="59">
            <span>秒</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* global AMap */
import PermissionService from '@/services/PermissionService'
import {
  lunarDateTextToSolarDate,
  formatSolarDateText,
  parseSolarDateText
} from '../../utils/lunarCalendar'

const phaseToleranceProtocol = require('../../utils/phaseToleranceProtocol')
const {
  normalizeDateInput,
  normalizeTimeInput,
  buildMaskedEditResult,
  getEditableCharRange
} = require('../../utils/astrolabeInteractionGuards')
const { formatFateDisplayData } = require('../../utils/fateDisplayFormat')

export default {
  name: 'AstrolabeForm',
  props: {
    requestData: Object,
    starList: {
      type: Array,
      default: () => []
    },
    fateData: {
      type: Object,
      default: () => ({})
    },
    natalFateData: {
      type: Object,
      default: () => ({})
    }
  },
  computed: {
    formattedFateData () {
      return formatFateDisplayData(this.fateData)
    },
    formattedNatalFateData () {
      return formatFateDisplayData(this.natalFateData)
    },
  },
  data () {
    return {
      map: null,
      dialogVisible: false,
      satelliteLayer: null,
      natalMap: null,
      natalDialogVisible: false,
      natalSatelliteLayer: null,
      natalPanelCollapsed: true, // 本命盘面板默认折叠

      // 地图「地名检索」（2026-10-10 新增）：
      // 详细地址输入框挂高德联想 + 「查询」/回车兜底检索；两个弹窗各一份实例与绑定标记
      autoComplete: null,
      natalAutoComplete: null,
      searchBound: false,
      natalSearchBound: false,

      timezones: [
        { value: -12, label: 'UTC-12 (贝克岛时间)' },
        { value: -11, label: 'UTC-11 (萨摩亚时间)' },
        { value: -10, label: 'UTC-10 (夏威夷时间)' },
        { value: -9, label: 'UTC-9 (阿拉斯加时间)' },
        { value: -8, label: 'UTC-8 (太平洋时间)' },
        { value: -7, label: 'UTC-7 (山地时间)' },
        { value: -6, label: 'UTC-6 (中部时间)' },
        { value: -5, label: 'UTC-5 (纽约时间)' },
        { value: -4, label: 'UTC-4 (大西洋时间)' },
        { value: -3, label: 'UTC-3 (布宜诺斯艾利斯时间)' },
        { value: -2, label: 'UTC-2 (南乔治亚时间)' },
        { value: -1, label: 'UTC-1 (亚速尔时间)' },
        { value: 0, label: 'UTC+0 (格林尼治时间)' },
        { value: 1, label: 'UTC+1 (巴黎时间)' },
        { value: 2, label: 'UTC+2 (开罗时间)' },
        { value: 3, label: 'UTC+3 (莫斯科时间)' },
        { value: 4, label: 'UTC+4 (迪拜时间)' },
        { value: 5, label: 'UTC+5 (卡拉奇时间)' },
        { value: 6, label: 'UTC+6 (阿拉木图时间)' },
        { value: 7, label: 'UTC+7 (曼谷时间)' },
        { value: 8, label: 'UTC+8 (北京时间)' },
        { value: 9, label: 'UTC+9 (东京时间)' },
        { value: 10, label: 'UTC+10 (悉尼时间)' },
        { value: 11, label: 'UTC+11 (所罗门群岛时间)' },
        { value: 12, label: 'UTC+12 (惠灵顿时间)' },
        { value: 13, label: 'UTC+13 (斐济时间)' },
        { value: 14, label: 'UTC+14 (基里巴斯时间)' }
      ],
      defaultMatrix: phaseToleranceProtocol.getDefaultLegacyToleranceMatrix(),
      strictInputCache: {
        date: '',
        time: '',
        natalDate: '',
        natalTime: ''
      }
    }
  },
  created () {
    const resolvedState = phaseToleranceProtocol.resolvePhaseToleranceState({
      toleranceMatrix: this.requestData.toleranceMatrix,
      phaseToleranceRows: this.requestData.phaseToleranceRows
    })
    this.$set(this.requestData, 'toleranceMatrix', resolvedState.toleranceMatrix)
    this.$set(this.requestData, 'phaseToleranceRows', resolvedState.phaseToleranceRows)
    this.initializeStrictInputFields()
  },
  mounted () {
    this.emitNatalPanelVisibility()
    // 窄屏（手机 / 竖屏旋转）下地图弹窗宽度需要跟着视口变，否则弹窗会窄到挤压内部排版
    window.addEventListener('resize', this.handleViewportResize)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.handleViewportResize)
  },
  watch: {
    'requestData.date': {
      handler (newVal) {
        const normalizedValue = this.syncStrictFieldFromState('date', newVal)
        if (normalizedValue) {
          this.updateLunarDate()
        }
      },
      immediate: true
    },
    'requestData.time': {
      handler (newVal) {
        this.syncStrictFieldFromState('time', newVal)
        this.$emit('request-fate-update')
      }
    },
    'requestData.timezone': {
      handler () {
        this.$emit('request-fate-update')
      }
    },
    'requestData.longitudeDeg': {
      handler () {
        this.$emit('request-fate-update')
      }
    },
    'requestData.longitudeMin': {
      handler () {
        this.$emit('request-fate-update')
      }
    },
    'requestData.longitudeSec': {
      handler () {
        this.$emit('request-fate-update')
      }
    },
    'requestData.latitudeDeg': {
      handler () {
        this.$emit('request-fate-update')
      }
    },
    'requestData.latitudeMin': {
      handler () {
        this.$emit('request-fate-update')
      }
    },
    'requestData.latitudeSec': {
      handler () {
        this.$emit('request-fate-update')
      }
    },
    'requestData.astrolabeType': {
      handler () {
        this.emitNatalPanelVisibility()
      },
      immediate: true
    },
    'requestData.natalDate': {
      handler (newVal) {
        if (this.syncStrictFieldFromState('natalDate', newVal)) {
          this.$emit('request-natal-fate-update')
        }
      },
      immediate: true
    },
    'requestData.natalTime': {
      handler (newVal) {
        this.syncStrictFieldFromState('natalTime', newVal)
        this.$emit('request-natal-fate-update')
      }
    },
    'requestData.natalLongitudeDeg': {
      handler () {
        this.$emit('request-natal-fate-update')
      }
    },
    'requestData.natalLongitudeMin': {
      handler () {
        this.$emit('request-natal-fate-update')
      }
    },
    'requestData.natalLongitudeSec': {
      handler () {
        this.$emit('request-natal-fate-update')
      }
    },
    'requestData.natalLatitudeDeg': {
      handler () {
        this.$emit('request-natal-fate-update')
      }
    },
    'requestData.natalLatitudeMin': {
      handler () {
        this.$emit('request-natal-fate-update')
      }
    },
    'requestData.natalLatitudeSec': {
      handler () {
        this.$emit('request-natal-fate-update')
      }
    }
  },
  methods: {
    hasPermission (key) {
      return PermissionService.hasPermission(key)
    },
    /**
     * 清空地图弹窗里的原生输入框（详细地址 / 纬度 / 经度）。
     * 这些 input 是原生 DOM、不受 Vue 响应式管理：点「确定」后值会一直留在 DOM 里，
     * 下次打开弹窗（以及刷新页面后浏览器恢复表单值）就会看到上一次的残留数据。
     * @param {Array<string>} ids - 需要清空的元素 id 列表
     */
    clearMapDialogInputs (ids = []) {
      ids.forEach((id) => {
        const el = document.getElementById(id)
        if (el) {
          el.value = ''
        }
      })
    },
    initMap () {
      // 创建地图实例（只创建一次；click 监听也一并只绑一次，
      // 避免每次打开弹窗重复 on('click') 叠加监听、一次点击反查多次地址）
      if (!this.map) {
        this.map = new AMap.Map('mapContainer', {
          resizeEnable: true, // 是否监控地图容器尺寸变化，默认false
          zoom: 11, // 初始化地图层级
          center: [116.397428, 39.90923] // 初始化地图中心点
        })
        this.satelliteLayer = new AMap.TileLayer.Satellite()
        this.map.on('click', (e) => {
          this.getAddress(e)
        })
      }
    },
    /**
     * 初始化「地名检索」：给详细地址输入框挂高德联想，选中建议即定位并回填经纬度。
     * 只绑定一次（searchBound 标记）；重复绑定会叠加多份监听、一次选择触发多次回填。
     */
    initMapSearch (scope) {
      const isNatal = scope === 'natal'
      if (isNatal ? this.natalSearchBound : this.searchBound) {
        return
      }
      const inputId = isNatal ? 'natalLatNum' : 'latNum'
      if (!document.getElementById(inputId)) {
        return
      }
      AMap.plugin('AMap.Autocomplete', () => {
        // 兼容 1.4（AMap.Autocomplete）与 2.0（AMap.AutoComplete）两种类名
        const AutoCompleteCtor = AMap.Autocomplete || AMap.AutoComplete
        if (!AutoCompleteCtor) {
          return
        }
        const autoComplete = new AutoCompleteCtor({
          input: inputId,
          city: '全国',
          citylimit: false
        })
        autoComplete.on('select', (e) => {
          const poi = (e && e.poi) || {}
          const lng = this.readLngLatValue(poi.location, 'lng')
          const lat = this.readLngLatValue(poi.location, 'lat')
          if (lng === null || lat === null) {
            return
          }
          this.applyMapPickerResult(scope, {
            address: `${poi.district || ''}${poi.name || ''}`.trim() || `${lng},${lat}`,
            lng,
            lat
          })
        })
        if (isNatal) {
          this.natalAutoComplete = autoComplete
          this.natalSearchBound = true
        } else {
          this.autoComplete = autoComplete
          this.searchBound = true
        }
      })
    },
    /** 从高德返回的 location（可能是 LngLat 实例或普通对象）里取经/纬度 */
    readLngLatValue (location, key) {
      if (!location) {
        return null
      }
      if (key === 'lng' && typeof location.getLng === 'function') {
        return location.getLng()
      }
      if (key === 'lat' && typeof location.getLat === 'function') {
        return location.getLat()
      }
      const value = Number(location[key])
      return Number.isFinite(value) ? value : null
    },
    /**
     * 输入框回车 / 点「查询」：按关键字检索并定位到首个匹配地点。
     * 联想下拉在手机上可能被键盘遮挡，这条路径保证"输入关键字也能查到"。
     */
    searchMapPlace (scope) {
      const isNatal = scope === 'natal'
      const inputEl = document.getElementById(isNatal ? 'natalLatNum' : 'latNum')
      const keyword = ((inputEl || {}).value || '').trim()
      if (!keyword) {
        return
      }
      AMap.plugin('AMap.PlaceSearch', () => {
        const placeSearch = new AMap.PlaceSearch({ city: '全国', citylimit: false, pageSize: 1 })
        placeSearch.search(keyword, (status, result) => {
          const pois = (result && result.poiList && result.poiList.pois) || []
          const poi = status === 'complete' ? pois[0] : null
          if (!poi) {
            if (this.$message) {
              this.$message.warning('没有查询到该地点，请换个关键字或直接点击地图选点')
            }
            return
          }
          const lng = this.readLngLatValue(poi.location, 'lng')
          const lat = this.readLngLatValue(poi.location, 'lat')
          if (lng === null || lat === null) {
            return
          }
          this.applyMapPickerResult(scope, {
            address: `${poi.pname || ''}${poi.cityname || ''}${poi.adname || ''}${poi.name || ''}`.trim() || `${lng},${lat}`,
            lng,
            lat
          })
        })
      })
    },
    /**
     * 把「地图选点 / 地名检索」的结果写进弹窗三个字段，并把地图定位到该点。
     * 字段映射沿用历史口径（见 closeMapDialog）：
     *   #latNum = 详细地址、#lat = 经度、#lng = 纬度。
     * 两种操作共用本方法 → 互相覆盖，以最后一次操作为准。
     */
    applyMapPickerResult (scope, { address, lng, lat }) {
      const isNatal = scope === 'natal'
      const [addressId, lngId, latId] = isNatal
        ? ['natalLatNum', 'natalLat', 'natalLng']
        : ['latNum', 'lat', 'lng']
      const setValue = (id, value) => {
        const el = document.getElementById(id)
        if (el) {
          el.value = value
        }
      }
      setValue(addressId, address)
      setValue(lngId, lng)
      setValue(latId, lat)
      const map = isNatal ? this.natalMap : this.map
      if (map && Number.isFinite(lng) && Number.isFinite(lat)) {
        map.setZoomAndCenter(15, [lng, lat])
      }
    },
    /** 点击地图选点 → 逆地理编码 → 覆盖详细地址框（与地名检索共用同一套回填） */
    getAddress (e) {
      const lng = e.lnglat.getLng()
      const lat = e.lnglat.getLat()
      AMap.plugin('AMap.Geocoder', () => { // 异步加载地理编码插件
        const geocoder = new AMap.Geocoder({
          // 不再固定 city：写死"北京"会让其他城市的逆地理结果被拉到北京口径
          radius: 1000 // 查询范围，单位米，可选参数
        })
        geocoder.getAddress([lng, lat], (status, result) => {
          const formattedAddress = status === 'complete' && result.regeocode
            ? result.regeocode.formattedAddress
            : ''
          this.applyMapPickerResult('main', {
            address: formattedAddress || `${lng},${lat}`,
            lng,
            lat
          })
        })
      })
    },

    /**
     * 十进制度 → 度 / 分 / 秒（秒保留两位小数）。
     * 浮点误差会把整分值算成「x 分 60.00 秒」（例如 39.9 → 39°53'60.00"），
     * 这里对 60 秒 / 60 分做进位归一，保证「打开地图→直接确定」不会把坐标显示改坏。
     * @param {number|string} coordinate - 十进制度
     * @returns {{degrees: number, minutes: number, seconds: number}|null}
     */
    decimalToDmsParts (coordinate) {
      const value = Number(coordinate)
      if (!Number.isFinite(value)) {
        return null
      }
      const absCoordinate = Math.abs(value) // 绝对值（沿用既有口径：不带半球符号）
      let degrees = Math.floor(absCoordinate) // 提取度
      let minutes = Math.floor((absCoordinate - degrees) * 60) // 提取分
      let seconds = Number((((absCoordinate - degrees) * 60 - minutes) * 60).toFixed(2)) // 提取秒
      if (seconds >= 60) {
        seconds = 0
        minutes += 1
      }
      if (minutes >= 60) {
        minutes -= 60
        degrees += 1
      }
      return { degrees, minutes, seconds }
    },
    latConvertToDMS (coordinate) {
      const parts = this.decimalToDmsParts(coordinate)
      if (!parts) {
        return
      }
      // 详细地址
      this.requestData.longitudeDeg = parts.degrees
      this.requestData.longitudeMin = parts.minutes
      this.requestData.longitudeSec = parts.seconds.toFixed(2)
    },
    lngConvertToDMS (coordinate) {
      const parts = this.decimalToDmsParts(coordinate)
      if (!parts) {
        return
      }
      // 详细地址
      this.requestData.latitudeDeg = parts.degrees
      this.requestData.latitudeMin = parts.minutes
      this.requestData.latitudeSec = parts.seconds.toFixed(2)
    },
    // 打开地图
    openMapDialog () { this.dialogVisible = true },
    /**
     * 读取右侧表单里已保存的十进制度坐标（主盘 / 本命盘）。
     * 用于「打开地图弹窗时按地址自动查询」：地图视野按地址查询结果居中，
     * 但弹窗里的坐标框仍回填配置里已有的值，
     * 避免「只打开弹窗看一下就点确定」把已有坐标改掉。
     * @param {string} scope - 'main' | 'natal'
     * @param {string} kind - 'longitude' | 'latitude'
     * @returns {number|null}
     */
    readSavedCoordinate (scope, kind) {
      const fieldPrefix = scope === 'natal' ? 'natal' : ''
      const readPart = (part) => {
        const raw = this.requestData[`${fieldPrefix}${kind}${part}`]
        if (raw === '' || raw === null || raw === undefined) {
          return null
        }
        const value = Number(raw)
        return Number.isFinite(value) ? value : null
      }
      const degrees = readPart('Deg')
      if (degrees === null) {
        return null
      }
      const minutes = readPart('Min') || 0
      const seconds = readPart('Sec') || 0
      const sign = degrees < 0 ? -1 : 1
      return sign * (Math.abs(degrees) + minutes / 60 + seconds / 3600)
    },
    /**
     * 打开地图弹窗时，按表单里当前地址自动在地图上查询定位（2026-10-17）。
     *
     * 背景：地址现在和其他表单字段一样会经 /api/user-config/load 回灌渲染，
     * 用户重新打开页面后点「地图」时不该再手工输入一遍地址。
     * 做法：把地址填进弹窗的「详细地址」框 → 地理编码（结构化地址更准）→
     *       失败则退回 POI 关键字检索（地名 / 地标）。
     * 边界：只动弹窗内的临时输入框与地图视野，不改表单里的地址与坐标；
     *       用户点「确定」后才按既有链路回写表单并触发自动保存。
     */
    locateAddressOnMap (scope = 'main') {
      const isNatal = scope === 'natal'
      const keyword = String((isNatal ? this.requestData.natalAddress : this.requestData.address) || '').trim()
      if (!keyword || typeof AMap === 'undefined') {
        return
      }
      const addressEl = document.getElementById(isNatal ? 'natalLatNum' : 'latNum')
      if (addressEl) {
        addressEl.value = keyword
      }
      AMap.plugin('AMap.Geocoder', () => {
        const geocoder = new AMap.Geocoder({ radius: 1000 })
        geocoder.getLocation(keyword, (status, result) => {
          const geocode = status === 'complete' && result && result.geocodes && result.geocodes[0]
          const lng = geocode ? this.readLngLatValue(geocode.location, 'lng') : null
          const lat = geocode ? this.readLngLatValue(geocode.location, 'lat') : null
          if (lng === null || lat === null) {
            // 结构化地址解析不出来（可能只写了地名/地标）→ 退回 POI 关键字检索
            this.searchMapPlace(scope)
            return
          }
          const savedLng = this.readSavedCoordinate(scope, 'longitude')
          const savedLat = this.readSavedCoordinate(scope, 'latitude')
          const hasSavedCoordinate = savedLng !== null && savedLat !== null
          this.applyMapPickerResult(scope, {
            address: keyword,
            lng: hasSavedCoordinate ? savedLng : lng,
            lat: hasSavedCoordinate ? savedLat : lat
          })
          if (hasSavedCoordinate) {
            // 坐标沿用配置值，地图视野仍按「地址查询结果」居中
            const map = isNatal ? this.natalMap : this.map
            if (map) {
              map.setZoomAndCenter(15, [lng, lat])
            }
          }
        })
      })
    },
    // 点击确定关闭，并获取选取数据
    closeMapDialog () {
      const addressText = document.getElementById('latNum').value
      const lngText = document.getElementById('lat').value // 经度（历史字段口径：#lat 存经度）
      const latText = document.getElementById('lng').value // 纬度（历史字段口径：#lng 存纬度）
      // 只输入了关键字、既没选点也没检索到位置（经纬度为空）时保持原值：
      // 否则会把空值按 0 写进 DMS，把已有坐标清成 0。
      if (!lngText && !latText) {
        this.dialogVisible = false
        this.clearMapDialogInputs(['latNum', 'lat', 'lng'])
        return
      }
      // 详细地址：只有确实拿到地址文本时才回写，避免把已有地址覆盖成空串
      if (addressText) {
        this.requestData.address = addressText
      }
      this.latConvertToDMS(lngText)
      this.lngConvertToDMS(latText)
      this.dialogVisible = false
      // 用完即清，防止下次打开/刷新后残留
      this.clearMapDialogInputs(['latNum', 'lat', 'lng'])
    },
    /**
     * 关闭地图弹窗（不应用，2026-10-14）：与右上角 × 同口径 —— 只关弹窗并清理临时输入，
     * **不回写**详细地址与经纬度。全屏后工具栏右端的「关闭」按钮走这里。
     */
    dismissMapDialog () {
      this.dialogVisible = false
      this.clearMapDialogInputs(['latNum', 'lat', 'lng'])
    },
    // 懒加载
    handLeDialogOpen () {
      this.$nextTick(() => {
        // 每次打开都先把上一次遗留的详细地址/经纬度清掉（含浏览器刷新后恢复的表单值）
        this.clearMapDialogInputs(['latNum', 'lat', 'lng'])
        this.initMap()
        // 给详细地址输入框挂「地名检索」（只绑一次）
        this.initMapSearch('main')
        // 表单里已有地址（用户输入或 /api/user-config/load 回灌）→ 打开即自动查询定位
        this.locateAddressOnMap('main')
      })
    },
    handleClose (done) {
      this.$confirm('确认关闭？')
        .then(_ => {
          done()
        })
        .catch(_ => {})
    },
    showMapType (type = Boolean) {
      console.log(type)
      // 构造官方卫星

      if (type) {
        this.map.add(this.satelliteLayer)
      } else {
        this.map.remove(this.satelliteLayer)
      }
    },

    // 公历 → 农历：由后端 /fate 统一计算（AstrolabeView.updateFateData 会回填 lunarDate），
    // 前端不重复实现，避免两套农历算法互相覆盖。
    updateLunarDate () {
      // 农历日期由后端计算，前端不再处理
    },
    // 农历 → 公历：用户手动输入「农历」（万年历口径）后，自动换算并写回对应的公历字段。
    // 写回后由既有 watcher 链路重新生成星盘/命理（后端再回填「星雲鑑历」）。
    // 换算与显示用的是同一套 lunar-calendar（东八区/24:00 口径），因此换算后
    // syncWannianliLunarDate 规范化出来的农历文本与用户输入一致（仅分隔符统一为半角）。
    // 注意：这里挂在 @input 上，只响应「用户输入」，不会被程序化写回触发，因此不会死循环。
    handleLunarTextInput (scope) {
      const isNatal = scope === 'natal'
      const lunarField = isNatal ? 'natalLunarDate' : 'lunarDate'
      const solarField = isNatal ? 'natalDate' : 'date'

      const lunarText = this.requestData[lunarField]
      if (!lunarText) {
        return
      }

      // 锚点：当前公历表单里的日期。
      // 用途一：干支年 60 年消歧；用途二：立春前后「干支年已换、农历年未换」时按就近日期择一。
      const anchor = parseSolarDateText(this.requestData[solarField]) ||
        { year: new Date().getFullYear(), month: 0, day: 0 }
      const solarDate = lunarDateTextToSolarDate(lunarText, anchor)
      if (!solarDate) {
        return
      }

      const solarText = formatSolarDateText(solarDate)
      if (!solarText || solarText === this.requestData[solarField]) {
        return
      }

      this.updateStrictFieldValue(solarField, solarText)
    },
    updateSolarDate () {
      // 保留方法以避免组件报错（农历换算已由 handleLunarTextInput 承担）
    },

    // 本命盘相关方法
    initNatalMap () {
      // 同主盘：地图与 click 监听都只创建/绑定一次，避免重复监听导致一次点击多次反查
      if (!this.natalMap) {
        this.natalMap = new AMap.Map('natalMapContainer', {
          resizeEnable: true,
          zoom: 11,
          center: [116.397428, 39.90923]
        })
        this.natalSatelliteLayer = new AMap.TileLayer.Satellite()
        this.natalMap.on('click', (e) => {
          this.getNatalAddress(e)
        })
      }
    },

    /** 本命盘地图：点击选点 → 逆地理编码 → 覆盖详细地址框（与地名检索共用同一套回填） */
    getNatalAddress (e) {
      const lng = e.lnglat.getLng()
      const lat = e.lnglat.getLat()
      AMap.plugin('AMap.Geocoder', () => {
        const geocoder = new AMap.Geocoder({
          // 不再固定 city：写死"北京"会让其他城市的逆地理结果被拉到北京口径
          radius: 1000
        })
        geocoder.getAddress([lng, lat], (status, result) => {
          const formattedAddress = status === 'complete' && result.regeocode
            ? result.regeocode.formattedAddress
            : ''
          this.applyMapPickerResult('natal', {
            address: formattedAddress || `${lng},${lat}`,
            lng,
            lat
          })
        })
      })
    },

    natalLatConvertToDMS (coordinate) {
      const parts = this.decimalToDmsParts(coordinate)
      if (!parts) {
        return
      }
      this.requestData.natalLongitudeDeg = parts.degrees
      this.requestData.natalLongitudeMin = parts.minutes
      this.requestData.natalLongitudeSec = parts.seconds.toFixed(2)
    },

    natalLngConvertToDMS (coordinate) {
      const parts = this.decimalToDmsParts(coordinate)
      if (!parts) {
        return
      }
      this.requestData.natalLatitudeDeg = parts.degrees
      this.requestData.natalLatitudeMin = parts.minutes
      this.requestData.natalLatitudeSec = parts.seconds.toFixed(2)
    },

    openNatalMapDialog () { this.natalDialogVisible = true },

    closeNatalMapDialog () {
      const addressText = document.getElementById('natalLatNum').value
      const lngText = document.getElementById('natalLat').value // 经度（历史字段口径：#natalLat 存经度）
      const latText = document.getElementById('natalLng').value // 纬度（历史字段口径：#natalLng 存纬度）
      // 只输入了关键字、没选点也没检索到位置时保持原值，避免把经纬度清成 0
      if (!lngText && !latText) {
        this.natalDialogVisible = false
        this.clearMapDialogInputs(['natalLatNum', 'natalLat', 'natalLng'])
        return
      }
      if (addressText) {
        this.requestData.natalAddress = addressText
      }
      this.natalLatConvertToDMS(lngText)
      this.natalLngConvertToDMS(latText)
      this.natalDialogVisible = false
      // 用完即清，防止下次打开/刷新后残留
      this.clearMapDialogInputs(['natalLatNum', 'natalLat', 'natalLng'])
    },
    /** 本命盘地图弹窗「关闭」（不应用）：口径同主盘 dismissMapDialog */
    dismissNatalMapDialog () {
      this.natalDialogVisible = false
      this.clearMapDialogInputs(['natalLatNum', 'natalLat', 'natalLng'])
    },

    handleNatalDialogOpen () {
      this.$nextTick(() => {
        // 每次打开都先清空上一次的残留
        this.clearMapDialogInputs(['natalLatNum', 'natalLat', 'natalLng'])
        this.initNatalMap()
        // 给详细地址输入框挂「地名检索」（只绑一次）
        this.initMapSearch('natal')
        // 本命盘口径同主盘：已有地址就自动查询定位
        this.locateAddressOnMap('natal')
      })
    },

    showNatalMapType (type = Boolean) {
      if (type) {
        this.natalMap.add(this.natalSatelliteLayer)
      } else {
        this.natalMap.remove(this.natalSatelliteLayer)
      }
    },

    updateNatalLunarDate () {
      // 农历日期由后端计算，前端不再处理
    },
    toggleNatalPanel () {
      this.natalPanelCollapsed = !this.natalPanelCollapsed
      this.emitNatalPanelVisibility()
    },
    /**
     * 窗口尺寸变化（2026-10-14）：
     * 地图弹窗改为全屏后不再需要按视口算弹窗宽度，改为让地图画布重算尺寸，
     * 否则窗口缩放后地图仍是旧尺寸、四周留空白。高德 Map 实例提供 resize 时才调用。
     */
    handleViewportResize () {
      [this.map, this.natalMap].forEach(instance => {
        if (instance && typeof instance.resize === 'function') {
          instance.resize()
        }
      })
    },
    emitNatalPanelVisibility () {
      const visible = ['ecliptic', 'horizon_ecliptic'].includes(this.requestData.astrolabeType) &&
        this.hasPermission('natal_chart') &&
        !this.natalPanelCollapsed
      this.$emit('natal-panel-visibility-change', visible)
    },
    initializeStrictInputFields () {
      ['date', 'time', 'natalDate', 'natalTime'].forEach(field => {
        this.syncStrictFieldFromState(field)
      })
    },
    getStrictFieldType (field) {
      return field.toLowerCase().includes('date') ? 'date' : 'time'
    },
    getStrictFieldDefault (field) {
      return this.getStrictFieldType(field) === 'date' ? '2000/01/01' : '00:00:00'
    },
    getStrictFieldFallback (field) {
      return this.strictInputCache[field] || this.requestData[field] || this.getStrictFieldDefault(field)
    },
    normalizeStrictFieldValue (field, rawValue, fallbackValue = this.getStrictFieldFallback(field), shouldPad = true) {
      if (this.getStrictFieldType(field) === 'date') {
        return normalizeDateInput(rawValue, fallbackValue, shouldPad)
      }
      return normalizeTimeInput(rawValue, fallbackValue, shouldPad)
    },
    syncStrictFieldFromState (field, rawValue = this.requestData[field]) {
      if (!rawValue) {
        return this.strictInputCache[field] || ''
      }

      const inputEl = this.$refs[field]
      const isEditing = inputEl && inputEl === document.activeElement
      const shouldPad = !isEditing

      const normalizedValue = this.normalizeStrictFieldValue(field, rawValue, this.getStrictFieldDefault(field), shouldPad)
      this.strictInputCache[field] = normalizedValue
      if (normalizedValue !== rawValue) {
        this.$set(this.requestData, field, normalizedValue)
      }
      return normalizedValue
    },
    updateStrictFieldValue (field, nextValue) {
      this.strictInputCache[field] = nextValue
      this.$set(this.requestData, field, nextValue)
    },
    moveStrictInputCaret (target, selectionStart, selectionEnd = selectionStart) {
      this.$nextTick(() => {
        if (target && typeof target.setSelectionRange === 'function') {
          target.setSelectionRange(selectionStart, selectionEnd)
        }
      })
    },
    handleStrictMaskedKeydown (event, field) {
      if (event.isComposing) {
        return
      }

      const lowerKey = event.key.toLowerCase()
      const fieldType = this.getStrictFieldType(field)

      if (['arrowleft', 'arrowright', 'arrowup', 'arrowdown', 'home', 'end', 'tab'].includes(lowerKey)) {
        return
      }

      if ((event.ctrlKey || event.metaKey) && ['a', 'c', 'v'].includes(lowerKey)) {
        return
      }

      const isSeparatorKey = event.key === (fieldType === 'date' ? '/' : ':')
      const isDateSignToggle = fieldType === 'date' && event.key === '-'
      const isDigitKey = /^\d$/.test(event.key)

      const isDeleteKey = lowerKey === 'backspace' || lowerKey === 'delete'
      if (!isDigitKey && !isSeparatorKey && !isDateSignToggle && !isDeleteKey) {
        event.preventDefault()
        return
      }

      event.preventDefault()
      const fallbackValue = this.getStrictFieldFallback(field)
      const currentValue = this.requestData[field] || fallbackValue
      const result = buildMaskedEditResult({
        type: fieldType,
        currentValue,
        key: event.key,
        selectionStart: event.target.selectionStart,
        selectionEnd: event.target.selectionEnd,
        fallbackValue
      })

      this.updateStrictFieldValue(field, result.value)
      this.moveStrictInputCaret(
        event.target,
        result.selectionStart == null ? result.caret : result.selectionStart,
        result.selectionEnd == null ? result.caret : result.selectionEnd
      )
    },
    handleStrictMaskedPaste (event, field) {
      event.preventDefault()
      const clipboardText = event.clipboardData ? event.clipboardData.getData('text') : ''
      if (!clipboardText) {
        return
      }

      const normalizedValue = this.normalizeStrictFieldValue(field, clipboardText, this.getStrictFieldFallback(field), false)
      this.updateStrictFieldValue(field, normalizedValue)
      this.moveStrictInputCaret(event.target, normalizedValue.length)
    },
    handleStrictMaskedInput (field) {
      const normalizedValue = this.normalizeStrictFieldValue(field, this.requestData[field], this.getStrictFieldFallback(field), false)
      this.updateStrictFieldValue(field, normalizedValue)

      if (field === 'date') {
        this.updateLunarDate()
      } else if (field === 'natalDate') {
        this.updateNatalLunarDate()
      }
    },
    // 验证日期输入格式（支持公元前日期，以负号开头）
    validateDateInput (field) {
      this.handleStrictMaskedInput(field)
    },
    // 验证时间输入格式（支持基本信息和本命盘）
    validateTimeInput (field = 'time') {
      this.handleStrictMaskedInput(field)
    },
    handleStrictMaskedFocus () {
      // Leave the browser's caret/selection intact so a single digit can be selected and replaced.
    },
    handleStrictMaskedMouseUp (event, field) {
      if (event.target.selectionStart !== event.target.selectionEnd) {
        return
      }
      const fieldType = this.getStrictFieldType(field)
      const range = getEditableCharRange(fieldType, event.target.value, event.target.selectionStart || 0)
      event.target.setSelectionRange(range.start, range.end)
    },
    handleStrictMaskedBlur (field) {
      const normalizedValue = this.normalizeStrictFieldValue(field, this.requestData[field], this.getStrictFieldFallback(field), true)
      this.updateStrictFieldValue(field, normalizedValue)
    }
  }
}
</script>

<style scoped>
input, select {
    padding: 6px;
    border: 1px solid #ddd;
    border-radius: 8px;
    width: 210px;
    font-size: 14px;
}

/* 减小表单行间距 */
.control-row {
    margin-bottom: 2px;
    display: flex;
    align-items: center;
}

.control-row label {
    min-width: 70px;
    flex-shrink: 0;
}
.info-row {
    margin-bottom: 2px;
    padding-left: 0;
    font-size: inherit;
    font-weight: 500;
    color: inherit;
    text-align: left;
}

/* 添加坐标输入样式 */
.coord-input {
  display: flex;
  align-items: center;
  gap: 5px;
}

/* 添加以下CSS规则移除数字输入框的箭头 */
.coord-input input[type="number"] {
  -moz-appearance: textfield; /* Firefox */
  -webkit-appearance: none; /* Chrome, Safari, Edge */
  appearance: textfield; /* 标准属性 */

  width: 45px !important; /* 增大宽度 */
  height: 28px !important; /* 增大高度 */
  padding: 5px !important; /* 增加内边距 */
  font-size: 14px !important; /* 增大字体 */
}

/* 针对Webkit浏览器隐藏内置箭头 */
.coord-input input[type="number"]::-webkit-outer-spin-button,
.coord-input input[type="number"]::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* 地址行（2026-10-17 改）：地址输入框必须与姓名 / 公历等普通输入框等宽（210px）。
   这一行只有「标签 70 + 输入框 210」的预算（表单内容宽约 310px），若再在右侧单独
   摆一个 56px 的「地图」按钮，必然溢出或被压缩成 160px 才能同行。
   因此「地图」按钮改为输入框内的右侧缀按钮（绝对定位在输入框右端），
   输入框用 padding-right 让出按钮位置：对外宽度与其他输入框完全一致，
   窄屏也不会换行、不会被压缩。 */
.address-field {
  position: relative;
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  min-width: 0;
}

.address-field .address-input {
  width: 210px;
  flex: 0 0 210px;
  min-width: 0;
  /* 右侧留给「地图」缀按钮，地址文本不会压在按钮下面 */
  padding-right: 62px;
  box-sizing: border-box;
}

/* 只覆盖地址行内的「地图」缀按钮，不影响弹窗里的「查询 / 确定 / 关闭」按钮 */
.address-field .mapButton {
  position: absolute;
  right: 3px;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 0;
  width: 52px;
  height: 26px;
  font-size: 12px;
}

.mapButton{
  margin-left: 3px;
  /* 宽高按 border-box 折算，保持与改动前一致的视觉尺寸（原 50+2*2+2 宽 / 30+2*2+2 高） */
  box-sizing: border-box;
  width: 56px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  background-color: #3498db;
  border: 1px #3498db ridge;
  padding: 2px;
  border-radius: 5px;
  color: white;
  /* 不参与收缩：空间不足时只缩输入框，按钮不会被挤走或压变形 */
  flex: none;
}
/* 详细地址框内的「查询」按钮：比弹窗右下角的「确定」更紧凑，
   避免在窄弹窗里把地址输入框挤没（不改动 .mapButton 本身，确定按钮观感不变） */
.mapButton--search {
  width: 44px;
  height: 30px;
  margin-left: 0;
  font-size: 12px;
}
#mapContainer {
  cursor: pointer;
}

/* 窄屏（手机）收紧弹窗内边距，并把地图容器高度调小，
   让弹窗尽量在一屏内放下，减少内部滚动造成的错位观感。
   地图容器的 500px 是写在行内的，覆盖需要 !important。 */
@media (max-width: 768px) {
  .el-dialog__wrapper >>> .el-dialog__body {
    padding: 10px;
  }
  .el-dialog__wrapper >>> #mapContainer,
  .el-dialog__wrapper >>> #natalMapContainer {
    height: 400px !important;
  }
  /* 手机上让「详细地址 + 纬度 + 经度 + 确定」四个元素保持同一行：
     缩小字段字号、缩短经纬度输入框，并让地址输入框优先让出空间
     （空间不足时由它压缩，而不是整行折行）。 */
  .map-dialog-fields {
    flex-wrap: nowrap;
  }
  .map-dialog-field span {
    font-size: 12px;
  }
  .map-dialog-field--address {
    flex: 1 1 90px;
  }
  .map-dialog-input--coord {
    width: 62px;
    flex: 0 1 62px;
  }
  .map-dialog-toolbar,
  .map-dialog-fields {
    gap: 6px;
  }
}
  /* 地图弹窗顶部信息行（详细地址 / 纬度 / 经度 / 确定）
     原来全部用 float 且容器写死 height: 40px：浮动项在窄弹窗里放不下就整体换行，
     固定高度又让溢出内容压到下方，表现为「输入框换行、确定按钮偏下」。
     改为 flex + wrap：放不下时整行正常折行，字段本身不再被拆散。 */
  /* 外层不换行：左侧字段区与右侧「确定」按钮始终同一行，按钮固定贴在右端。
     字段放不下时只在 .map-dialog-fields 内部折行，不会把按钮挤到下一行。 */
  .map-dialog-toolbar {
    display: flex;
    flex-wrap: nowrap;
    align-items: center;
    gap: 8px;
    margin-bottom: 10px;
  }
  .map-dialog-fields {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px 8px;
    flex: 1 1 auto;
    min-width: 0;
  }
  .map-dialog-field {
    display: flex;
    align-items: center;
    gap: 4px;
    min-width: 0;
  }
  .map-dialog-field span {
    font-size: 15px;
    white-space: nowrap;
  }
  /* 详细地址较长，吃掉剩余宽度；空间不够时由它优先收缩 */
  .map-dialog-field--address {
    flex: 1 1 240px;
  }
  /* 覆盖组件内 input 默认的 210px 固定宽度，改为随容器伸缩 */
  .map-dialog-input {
    flex: 1 1 auto;
    width: auto;
    min-width: 0;
    height: 30px;
    border: #d9d9d9 1px solid;
  }
  /* 经纬度数值较短，给固定基准宽度即可 */
  .map-dialog-input--coord {
    flex: 0 1 115px;
    width: 115px;
  }

  /* 地图容器定位父级：让卫星地图开关能浮在地图内部 */
  .map-container-wrapper {
    position: relative;
  }

  /* 卫星地图开关：浮在地图内部左上角（高德示例的经典位置）。
     原样式是 position: fixed 的卡片，脱离文档流后会飘到地图外面。 */
  .input-card{
    position: absolute;
    top: 10px;
    left: 10px;
    z-index: 10;
    display: flex;
    align-items: center;
    gap: 6px;
    min-width: 0;
    word-wrap: break-word;
    background-color: #fff;
    background-clip: border-box;
    border-width: 0;
    box-shadow: 0 2px 6px 0 rgba(114, 124, 245, .5);
    padding: 4px 8px;
    width: fit-content;
    max-width: calc(100% - 20px);
  }
 .btn{
   margin-left: 3px;
   /* 宽高按 border-box 折算，与改动前视觉尺寸一致（原 50+2*2+2 宽 / 25+2*2+2 高） */
   box-sizing: border-box;
   width: 56px;
   height: 31px;
   display: flex;
   align-items: center;
   justify-content: center;
   cursor: pointer;
   background-color: #3498db;
   border: 1px #3498db ridge;
   padding: 2px;
   border-radius: 5px;
   color: white;
  font-weight: 200;
  white-space: nowrap;
  /* 不收缩：保证「打开 / 关闭」始终同行、不被压变形 */
  flex: none;
  }

.fate-info {
  color: #2c3e50;
  word-break: break-word;
  padding: 6px;
  display: inline-block;
  font-size: 14px;
}

.collapse-btn {
  cursor: pointer;
  color: #3498db;
  font-size: 14px;
  font-weight: normal;
  margin-left: 10px;
}

.collapse-btn:hover {
  color: #2980b9;
}

/* 右侧表单：「星雲鑑历」（标签 + 只读输入框文字）与「四柱」用先天宫「申子辰」的蓝色（2026-10-15）。
   色值取自 AstrolabeDrawer 的 styles.signCircle.fontColor（申 / 子 / 辰 = rgba(0, 0, 255, 1)）。
   注：星雲鑑历输入框自身带行内 style（行内优先于样式表），其文字色写在模板的 style 属性里；
   这里负责标签与「四柱」的取色。 */
.info-row--four-pillars,
.info-row--four-pillars span,
.label--xiangyunjianli {
  color: rgba(0, 0, 255, 1);
}

/* 星雲鑑历（2026-10-18）：该行只做文本展示，不再用只读输入框。
   宽度与普通输入框一致（210px），并与输入框共用同一列右对齐，
   行高按输入框高度对齐，保证右侧表单纵向仍然整齐；
   空值显示灰色「待计算」，与改造前的 placeholder 观感一致。 */
.control-row__text {
  width: 210px;
  flex: 0 0 210px;
  min-height: 31px;
  display: flex;
  align-items: center;
  font-size: 14px;
  line-height: 1.4;
  word-break: break-all;
}

.control-row__text--xyj {
  color: rgba(0, 0, 255, 1);
}

.control-row__text--empty {
  color: #909399;
}

/* 全屏地图弹窗右端的「关闭」按钮：中性灰，不与主操作「确定」抢视觉 */
.mapButton--close {
  background-color: #909399;
  border-color: #909399;
  margin-left: 8px;
}
</style>

<style>
/* ------------------------------------------------------------------
   全屏地图弹窗（2026-10-14）
   弹窗本体用 el-dialog 的 fullscreen + custom-class="map-dialog-fullscreen"；
   这里只补「布局」：工具栏固定高度、地图区吃掉剩余高度
   （原来地图写死 height:500px，全屏后底部会留一大片空白）。
   注意：.map-dialog-fullscreen / .el-dialog__body / .el-dialog__header
   由 Element UI 渲染、不带 scoped 属性，所以必须放在非 scoped 样式块里；
   用 custom-class 限定作用域，不影响其它弹窗。
   ------------------------------------------------------------------ */
.map-dialog-fullscreen {
  display: flex;
  flex-direction: column;
  height: 100%;
  margin: 0;
  overflow: hidden;
}

.map-dialog-fullscreen .el-dialog__header {
  flex: none;
  padding: 12px 20px;
}

.map-dialog-fullscreen .el-dialog__body {
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  padding: 12px 20px 16px;
  overflow: hidden;
}

.map-dialog-fullscreen .el-dialog__body > .map-dialog-toolbar {
  flex: none;
}

.map-dialog-fullscreen .el-dialog__body > .map-container-wrapper {
  flex: 1 1 auto;
  min-height: 320px;
}

/* 地图画布填满容器 */
.map-dialog-fullscreen .map-canvas {
  width: 100%;
  height: 100%;
  min-height: 320px;
}
</style>
